// Offline-first sync queue for contractor field actions.
// Actions are stored in localStorage with unique action_ids (idempotency keys) and
// flushed to POST /api/contractor/sync when online. Photos are queued as data URLs.
import api from "@/lib/api";

const KEY = "sj_sync_queue";
const listeners = new Set();

export function uid() {
  return (crypto?.randomUUID ? crypto.randomUUID() : `a-${Date.now()}-${Math.random().toString(16).slice(2)}`);
}
function read() { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } }
function write(q) { localStorage.setItem(KEY, JSON.stringify(q)); listeners.forEach((l) => l()); }

export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function pending() { return read().filter((a) => a.status !== "synced"); }
export function allActions() { return read(); }
export function isOnline() { return navigator.onLine; }

export function enqueue(type, work_order_id, payload = {}, kind = "action") {
  const q = read();
  const action = { action_id: uid(), type, work_order_id, payload, kind, device_time: new Date().toISOString(), status: "pending", retry: 0, needs_review: false, error: null };
  q.push(action);
  write(q);
  flush();
  return action.action_id;
}

async function _syncPhoto(a) {
  const fd = new FormData();
  const blob = await (await fetch(a.payload.dataUrl)).blob();
  fd.append("file", blob, `${a.action_id}.jpg`);
  fd.append("work_order_id", a.work_order_id);
  fd.append("phase", a.payload.phase || "progress");
  fd.append("client_ref", a.action_id);
  await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
}

let flushing = false;
export async function flush() {
  if (flushing || !navigator.onLine) return;
  const q = read();
  const todo = q.filter((a) => a.status === "pending" || a.status === "failed");
  if (todo.length === 0) return;
  flushing = true;
  try {
    // Photos first (so 'complete' validation sees them), then batched actions.
    for (const a of todo.filter((x) => x.kind === "photo")) {
      try { await _syncPhoto(a); mark(a.action_id, "synced"); }
      catch (e) { mark(a.action_id, "failed", e.response?.data?.detail || "upload failed"); }
    }
    const acts = read().filter((a) => (a.status === "pending" || a.status === "failed") && a.kind === "action");
    if (acts.length) {
      const { data } = await api.post("/contractor/sync", { actions: acts.map((a) => ({ action_id: a.action_id, work_order_id: a.work_order_id, type: a.type, device_time: a.device_time, payload: a.payload })) });
      (data.results || []).forEach((r) => {
        if (r.status === "synced") mark(r.action_id, "synced");
        else if (r.status === "needs_review") mark(r.action_id, "needs_review", r.error);
        else mark(r.action_id, "failed", r.error);
      });
    }
  } catch (e) {
    // network dropped mid-flush; leave items pending for next attempt
  } finally {
    flushing = false;
    // prune synced older than nothing — keep last 50 for history
    const cur = read();
    if (cur.length > 100) write(cur.slice(-100));
  }
}

function mark(action_id, status, error = null) {
  const q = read();
  const a = q.find((x) => x.action_id === action_id);
  if (a) { a.status = status; a.error = error; a.needs_review = status === "needs_review"; if (status === "failed") a.retry = (a.retry || 0) + 1; write(q); }
}

export function clearSynced() { write(read().filter((a) => a.status !== "synced")); }

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { listeners.forEach((l) => l()); flush(); });
  window.addEventListener("offline", () => listeners.forEach((l) => l()));
  setInterval(() => { if (navigator.onLine) flush(); }, 20000);
}
